<script setup lang="ts">
import {
  Mate,
  MateDispatch,
  MateDispatchMember,
  MateEquipment,
  MateRelationship,
} from "@/types/entity";
import {
  DispatchDifficultyLevel,
  DispatchType,
  InvitationType,
  RelationshipType,
} from "@/enums/mission";
import { useName } from "@/use/common";
import { useRouter } from "vue-router";
import { useGlobalModal } from "@/stores/globalModal";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { useInteractive } from "@/stores/interactive";
import { useI18n } from "vue-i18n";
import { useMate } from "@/stores/mate";
import { computed, onMounted, ref } from "vue";
import { useMission } from "@/stores/mission";
import gsap from "gsap";
import { DispatchMemberPayerType, DispatchMemberType } from "@/enums/mateEnums";
import { fixNumber } from "./MissionStat";
import { useGlobalUser } from "@/stores/globalUser";

const props = defineProps<{
  dispatchMission: Partial<MateDispatch>;
}>();

const router = useRouter();
const modalStore = useGlobalModal();
const interactiveStore = useInteractive();
const mateStore = useMate();
const missionStore = useMission();
const { t } = useI18n();

const showEquipmentModal = ref(false);
const selectedEquipment = ref<MateEquipment>({} as MateEquipment);

const displayScore = ref(0);

const isHost = computed(() => {
  const host = props.dispatchMission.dispatchMemberzz?.find(
    (v) => v.type === DispatchMemberType.Leader
  );
  if (!host) return false;

  return mateStore.isMyMateById(host.mateId);
});

onMounted(() => {
  if (props.dispatchMission.obtainedScore) {
    gsap.to(displayScore, {
      value: props.dispatchMission.obtainedScore,
      duration: 2,
      ease: "power1.out",
      onUpdate: () => {
        displayScore.value = Math.round(displayScore.value);
      },
    });
  }
});

const sendMail = (
  item: Mate & { targetRelationshipzz: MateRelationship[] }
) => {
  const relationship = item.targetRelationshipzz[0];
  if (!relationship.sourceMate || !relationship.targetMate) return;

  router.push({
    name: "mailEdit",
    query: {
      senderId: relationship.sourceMate.id.toString(),
      recipientId: relationship.targetMate.id.toString(),
      recipientName: relationship.targetMate.name,
    },
  });
};

const toggleFriend = async (
  item: Mate & { targetRelationshipzz: MateRelationship[] }
) => {
  const relationship = item.targetRelationshipzz[0];
  if (!relationship.sourceMate || !relationship.targetMate) return;

  if (relationship.type === RelationshipType.Acquaintance) {
    modalStore.openTextInputModal({
      title: "申请添加朋友",
      callback: async (text) => {
        const loading = showLoadingToast({
          message: "申请中...",
          duration: 0,
        });
        try {
          await interactiveStore.createInvitation({
            senderId: relationship.sourceMate!.id,
            recipientId: relationship.targetMate!.id,
            type: InvitationType.Friend,
            message: text,
          });
        } finally {
          loading.close();
        }
      },
    });
  } else if (relationship.type === RelationshipType.Friend) {
    // 取消好友
    try {
      await showConfirmDialog({
        message: t("Message.deleteFriendConfirm"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
      await interactiveStore.cancelFriend(relationship.id);
      showSuccessToast(t("Message.opSuccess"));
    } catch (error) {
      console.error("Failed to delete relationship", error);
    }
  }
};

const inviteTask = () => {
  const { metCharacterzz, mateId } = props.dispatchMission;
  router.push({
    name: "missionEdit",
    query: {
      hostUuid: mateStore.getMyMateById(mateId!)?.uuid,
      inviteeUuid: metCharacterzz![0].uuid,
      type: DispatchType.Mission,
    },
  });
};

const clickEquipment = (item: MateEquipment) => {
  selectedEquipment.value = item;
  showEquipmentModal.value = true;
};

const payerCount = computed(() => {
  let count = 1;
  props.dispatchMission.dispatchMemberzz?.forEach((v) => {
    if (v.payerType === DispatchMemberPayerType.Host) {
      count++;
    }
  });

  return count;
});

const clickRetry = async () => {
  const { dispatchMemberzz } = props.dispatchMission;
  const hasOtherPayer = dispatchMemberzz?.some(
    (v) => v.type === DispatchMemberType.OthersCharacter
  );

  if (hasOtherPayer) {
    showParticipateModal.value = true;
  } else {
    retryRequest();
  }
};

const retryRequest = async () => {
  const { id, costPowerCubes, dispatchMemberzz } = props.dispatchMission;
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: costPowerCubes! * payerCount.value,
      cb: clickRetry,
    })
  )
    return;

  const loading = showLoadingToast({
    message: "重试中...",
    duration: 0,
  });

  try {
    await missionStore.retryDispatchMission(id!, {
      memberzz: dispatchMemberzz?.map(
        (v) =>
          ({
            mateId: v.mate?.id,
            payerType: v.payerType,
          } as MateDispatchMember)
      ),
    });
    loading.close();
    useGlobalUser().refresh();
    showSuccessToast(t("Message.opSuccess"));
    router.replace({
      name: "missionHome",
    });
  } catch {
    loading.close();
  }
};

const retryAddRate = computed(() => {
  let rate = 2;
  const level = props.dispatchMission.difficultyLevel;
  switch (level) {
    case DispatchDifficultyLevel.Nightmare:
      rate = 1;
      break;
    case DispatchDifficultyLevel.Hell:
      rate = 0.5;
      break;
  }

  return rate;
});

const showParticipateModal = ref(false);
</script>
<template>
  <div class="bg-[#4F2CE8] border-4 border-[#3604A8] rounded-xl relative">
    <div class="flex flex-col items-center gap-2 p-2">
      <!-- 任务目标 -->
      <div v-if="dispatchMission.difficultyLevel" class="mt-6">
        <div
          class="flex items-center absolute -top-2 left-[50%] transform -translate-x-1/2 -translate-y-1/2 w-full"
        >
          <img
            v-if="dispatchMission.isSuccess"
            src="/img/ribbons/success_ribbon.png"
            alt=""
            class="h-28 w-full object-contain"
          />
          <img
            v-else
            src="/img/ribbons/fail_ribbon.png"
            style="filter: grayscale(100%)"
            alt=""
            class="h-28 w-full object-contain"
          />
        </div>
        <!-- 角色头像 -->
        <div class="flex justify-center gap-8">
          <div
            v-for="item in dispatchMission.dispatchMemberzz"
            :key="item.id"
            class="flex flex-col items-center gap-1.5 justify-center"
          >
            <div
              class="flex flex-col w-14 border-4 border-[#3604A8] rounded after:content-[''] after:w-full after:h-1 after:bg-[#3504A8]"
            >
              <UserAvatar :mate="item.mate!" class="" />
            </div>
            <p class="line-clamp-1 text-sm relative text-center w-full">
              {{ useName(item.mate!.name) }}
            </p>
          </div>
        </div>
        <div
          class="text-center text-sm mt-2 flex flex-col gap-2"
          v-if="dispatchMission.isRankingEnabled"
        >
          <div
            class="text-2xl font-bold text-[#FDD340] [text-shadow:_2px_2px_2px_rgba(0,0,0,0.5)] score-float"
          >
            {{ displayScore }} 分
          </div>
          <MissionEditBottomSeasonScore
            :dispatch-mission="dispatchMission"
            :show-detail="true"
            :hide-score="true"
            enable-animation
          />
        </div>
      </div>

      <!-- 奖励文本 -->
      <div class="text-center text-lg font-bold">
        {{
          dispatchMission.type === DispatchType.Stroll
            ? $t(`mission.journey.receivedStroll`)
            : $t(`mission.journey.received`, {
                count: dispatchMission?.obtainedJournalzz?.length,
                countEquipment: dispatchMission?.obtainedEquipmentzz?.length,
              })
        }}
      </div>

      <!-- 结识 OC -->
      <div
        class="text-center text-lg font-bold flex flex-col gap-2"
        v-if="dispatchMission.type === DispatchType.Stroll"
      >
        <div
          v-for="item in dispatchMission.metCharacterzz"
          :key="item.id"
          class="flex items-center gap-2"
        >
          <div
            class="w-12 flex flex-col items-center gap-1"
            @click="
              router.push({
                name: 'mateHome',
                params: { uuid: item.uuid },
              })
            "
          >
            <UserAvatar :mate="item" class="w-full" />
            <p class="line-clamp-1 text-xs">{{ useName(item.name) }}</p>
          </div>

          <template v-if="item.targetRelationshipzz.length">
            <div class="flex flex-col gap-1 items-start">
              <div class="flex items-center gap-2">
                <button class="btn btn-xs" @click="sendMail(item)">
                  <i class="iconify fluent--mail-16-regular"></i>
                  {{ $t("interactive.sendMail") }}
                </button>

                <button
                  class="btn btn-xs"
                  @click="toggleFriend(item)"
                  v-if="
                    !item.targetRelationshipzz[0].isBlocked &&
                    item.targetRelationshipzz[0].type ===
                      RelationshipType.Acquaintance
                  "
                >
                  {{ $t("interactive.inviteFriend") }}
                </button>
              </div>
              <div class="flex gap-2">
                <button class="btn btn-xs" @click="inviteTask">
                  {{ $t("interactive.inviteTask") }}
                </button>
                <GoInteractionHistory
                  class="btn btn-xs"
                  :source-mate-id="item.targetRelationshipzz[0].sourceMateId"
                  :target-mate-id="item.targetRelationshipzz[0].targetMateId"
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 物品图标 -->
      <div class="flex gap-2">
        <div
          class="flex flex-col items-center w-10 gap-1"
          v-for="item in dispatchMission?.obtainedEquipmentzz"
          :key="item.id"
        >
          <EquipmentIcon
            :equipment="item"
            @click="clickEquipment(item)"
            class="border-4 border-[#06016E] rounded after:content-[''] after:w-full after:h-1 after:bg-[#3504A8] bg-[#3504A8]"
          />
          <span class="text-sm">
            {{ $t(`mission.equipment.rarity.${item.rarity}`) }}
          </span>
        </div>
      </div>
      <div
        class="flex flex-col items-center justify-center gap-2"
        v-if="
          !dispatchMission.isSuccess &&
          dispatchMission.isRankingEnabled &&
          isHost &&
          dispatchMission.canRetry
        "
      >
        <button
          class="bg-[#27CEA4] text-white border-2 border-[#0F5878] flex rounded-lg text-sm items-center gap-2 px-4 py-2 justify-center"
          @click="clickRetry"
        >
          <!-- <i class="iconify mingcute--refresh-3-line w-6"></i> -->
          重试
          <div
            class="flex items-center gap-1"
            v-if="
              !dispatchMission.dispatchMemberzz?.some(
                (v) => v.type === DispatchMemberType.OthersCharacter
              )
            "
          >
            <PowerIcon />
            {{ dispatchMission.costPowerCubes }}
          </div>
        </button>
        <p class="text-xs text-white/50 mt-1">
          重试任务成功率 +{{ retryAddRate }}%
        </p>
      </div>
    </div>
    <EquipmentDetailModal
      v-model:show="showEquipmentModal"
      :equipment="selectedEquipment"
      class="text-base-content"
      mode="view"
    />
    <VanPopup v-model:show="showParticipateModal" class="w-full p-4">
      <div class="flex flex-col gap-2 text-center mb-6">
        <p>请选择是否帮 Ta 支付能量块</p>
      </div>
      <MissionParticipantsSelect
        :dispatch-mission="dispatchMission"
        :is-edit="false"
        type="retry"
        show-action
      />

      <div class="flex justify-end gap-2">
        <button class="btn btn-sm btn-primary" @click="retryRequest">
          确定
          <PowerIcon />
          {{ fixNumber(payerCount * (dispatchMission.costPowerCubes ?? 0)) }}
        </button>
      </div>
    </VanPopup>
  </div>
</template>
<style scoped>
.parallelogram {
  --p: 30px; /* control the shape (can be percentage) */
  aspect-ratio: 3/4;
  clip-path: polygon(var(--p) 0, 100% 0, calc(100% - var(--p)) 100%, 0 100%);
  background-size: cover;
  border-radius: 5px;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.score-float {
  animation: float 2s ease-in-out infinite;
}
</style>
